import React, { useState } from "react";
import {
  Avatar,
  Button,
  Row,
  Col,
  Typography,
  Spin,
  message,
  Modal,
  Input,
} from "antd";
import {
  CreditCardOutlined,
  WarningOutlined,
  EditFilled,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, Navigate } from "react-router-dom";
import {
  ROUTE_PROFILE_EDIT,
  ROUTE_CLASSROOM,
  ROUTE_PAYMENTS,
} from "../../constant/routes";
import "./Profile.less";
import {
  useGetUserByIDQuery,
  useReportIssueMutation,
} from "../../redux/services/user";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/features/authSlice";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;
const { TextArea } = Input;

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { student_id } = useSelector((state) => state.auth);

  // Fetch user details
  const {
    data: user,
    error: userError,
    isLoading: userLoading,
  } = useGetUserByIDQuery(student_id);

  // Report issue API mutation
  const [reportIssue, { isLoading: reportLoading, error: reportError }] =
    useReportIssueMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");

  const handleLogout = () => {
    dispatch(logout());
    message.success("You have been logged out successfully.");
  };

  const handleReportSubmit = async () => {
    const payload = {
      student_assoc: student_id,
      subject,
      description,
    };

    try {
      const response = await reportIssue(payload).unwrap();
      if (response?.status_code === 200) {
        message.success(response?.message || "Issue reported successfully!");
        setIsModalOpen(false);
        setSubject("");
        setDescription("");
      } else {
        setIsModalOpen(false);
        setSubject("");
        setDescription("");
        message.error(response?.message || "Failed to report the issue.");
      }
    } catch (error) {
      setIsModalOpen(false);
      setSubject("");
      setDescription("");
      message.error(error);
    }
  };

  return (
    <>
      {/* Loader for user data */}

      <div className='profile-container'>
        {/* Display error for user data */}
        {userError &&
          message.error(userError.message || "Failed to load user details.")}

        {/* User Info Section */}
        <Spin spinning={userLoading}>
          <Row
            justify='space-between'
            style={{ display: "flex", alignItems: "center" }}
          >
            <Col>
              <Avatar
                size={100}
                src={user?.user?.profile_img}
                icon={user?.user?.profile_img ? null : <UserOutlined />}
              />
            </Col>
            <Col className='profile-info'>
              <h2 className='name'>{user?.user?.name?.toUpperCase()}</h2>
              <p className='email'>{user?.user?.email}</p>
            </Col>
            <Col>
              <Link to={{ pathname: ROUTE_PROFILE_EDIT, state: { user } }}>
                <Button icon={<EditFilled />} type='text'></Button>
              </Link>
            </Col>
          </Row>
        </Spin>

        {/* Dashboard List */}
        <div className='dashboard'>
          <Row>
            <Title className='title'>Dashboard</Title>
          </Row>
          <Row
            justify='space-between'
            style={{ marginTop: "20px", alignItems: "center" }}
          >
            <Row>
              <Avatar
                size={60}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#0277C6" }}
              />
              <h3 style={{ marginLeft: "20px" }}>My Classes</h3>
            </Row>
            <Col>
              <Button
                icon={<RightOutlined />}
                type='text'
                onClick={() => navigate(ROUTE_CLASSROOM)}
              />
            </Col>
          </Row>
          <Row
            justify='space-between'
            style={{ marginTop: "20px", alignItems: "center" }}
          >
            <Row>
              <Avatar
                size={60}
                icon={<CreditCardOutlined />}
                style={{ backgroundColor: "#59BD7D" }}
              />
              <h3 style={{ marginLeft: "20px" }}>My Payments</h3>
            </Row>
            <Col>
              <Button
                icon={<RightOutlined />}
                type='text'
                onClick={() => navigate(ROUTE_PAYMENTS)}
              />
            </Col>
          </Row>
          <Row
            justify='space-between'
            style={{ marginTop: "20px", alignItems: "center" }}
          >
            <Row>
              <Avatar
                size={60}
                style={{ backgroundColor: "red" }}
                icon={<WarningOutlined />}
              />
              <h3 style={{ marginLeft: "20px" }}>Report Issue</h3>
            </Row>
            <Col>
              <Button
                icon={<RightOutlined />}
                type='text'
                onClick={() => setIsModalOpen(true)}
              />
            </Col>
          </Row>
        </div>

        {/* Account Options */}
        <div className='profile-account-options'>
          <Title className='title'>My Account</Title>
          <Button className='account-button' type='link'>
            Switch to Other Account
          </Button>
          <Button className='account-button' type='link' onClick={handleLogout}>
            Log Out
          </Button>
        </div>
      </div>

      {/* Report Issue Modal */}
      <Modal
        centered
        title='Report an Issue'
        open={isModalOpen}
        onOk={handleReportSubmit}
        closable={true}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={reportLoading}
      >
        <Input
          placeholder='Enter subject'
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <TextArea
          rows={4}
          placeholder='Describe the issue'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default Profile;
